import { Group, createStyles } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { Button } from '~/domains/design-system/Button';
import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import MessageBanner, { BannerType } from '~/domains/common/components/MessageBanner/MessageBanner';

export type GamecenterStandingsExportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  url: string;
};

const useStyles = createStyles((theme) => ({
  body: {
    width: '100vw',

    [theme.fn.largerThan('sm')]: {
      maxWidth: 420,
    },
  },
  title: {
    textAlign: 'center',
    padding: `0 ${theme.other.spacing._32}px`,
  },
  description: {
    paddingTop: theme.other.spacing._8,
    paddingBottom: theme.other.spacing._16,
    color: theme.colors.gray[7],
    textAlign: 'center',
  },
}));

function GamecenterStandingsExportModal({
  isOpen,
  onClose,
  url,
}: GamecenterStandingsExportModalProps) {
  const { t } = useTranslation('contest');
  const { classes, theme } = useStyles();

  const handleDownload = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <Dialog
      type={{
        desktop: 'modal',
        mobile: 'drawer',
      }}
      isOpen={isOpen}
      onClose={onClose}
    >
      <DialogBody className={classes.body}>
        <Title variant="headline-xl" className={classes.title}>
          {t('gamecenter.standingsExport.modal.title')}
        </Title>

        <Text tag="div" className={classes.description} variant="body-large">
          {t('gamecenter.standingsExport.modal.description')}
        </Text>

        <MessageBanner
          type={BannerType.INFO}
          message={t('gamecenter.standingsExport.modal.info')}
        />

        <Group mt={theme.other.spacing._16}>
          <Button variant="primary-fill" onClick={handleDownload} fullWidth>
            <Text variant="label-large">{t('gamecenter.standingsExport.modal.downloadCta')}</Text>
          </Button>

          <Button variant="tertiary-fill" onClick={onClose} fullWidth>
            <Text variant="label-large" color={theme.colors.gray2[7]}>
              {t('gamecenter.standingsExport.modal.closeCta')}
            </Text>
          </Button>
        </Group>
      </DialogBody>
    </Dialog>
  );
}

export default GamecenterStandingsExportModal;
